import { useRef, useState } from 'react';
import { Anchor, Button, FileButton, Group, Stack, Table, Text } from '@mantine/core';
import type { ActionAttachmentDetail, ActionDetail } from '@isg/shared-types';
import { AKSIYON_EK_LIMIT, DOSYA_MAX_BOYUT_BYTES } from '@isg/validation';
import { useAksiyonEkleri, useEkIndir, useEkSil, useEkYukle } from '../api/queries';
import { QueryStatus } from './parts';
import { KapiSebebi, SatirSilmeSebebi } from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * Aksiyon kanıt ekleri bölümü (F2·S3 — Kapsam §22.4). Düğme görünürlüğü
 * role/duruma göre ama KARAR SUNUCUDA (§2.16): ekran yalnız 403/409 alacak
 * düğmeyi baştan göstermez.
 *
 *  - Yükle: yalnız SORUMLU (atanan), yalnız kapalı OLMAYAN aksiyonda.
 *  - Sil: yalnız YÜKLEYEN, yalnız kapalı olmayan aksiyonda (kapalı aksiyonun
 *    kanıtı değişmez — §22.4/4).
 *  - İndir: aksiyonu görebilen herkes (liste zaten sunucu filtresinden gelir).
 *
 * Thumbnail/önizleme v1'de YOK (bilinçli — indirme yeterli).
 */
export function AksiyonEkBolumu({ aksiyon }: { aksiyon: ActionDetail }): React.JSX.Element {
  const ekler = useAksiyonEkleri(aksiyon.id);
  const yukle = useEkYukle();
  const sil = useEkSil();
  const indir = useEkIndir();
  const sifirla = useRef<() => void>(null);
  const [yerelHata, setYerelHata] = useState<string | null>(null);

  // ⚠️ T2: yükleme hakkı SUNUCUDAN (`yetkiler.ekYukleyebilir`) — "kapalı
  // değil ∧ atanan benim" koşulunu burada ikinci kez yazmak, kural
  // değiştiğinde ekranın eski cümleyi konuşması demekti (§2.40).
  const yukleyebilir = aksiyon.yetkiler.ekKapisi.yazabilir;
  const doluluk = ekler.data?.length ?? 0;

  return (
    <Stack gap="xs" data-testid="aksiyon-ek-bolumu">
      <Group justify="space-between">
        <Text fw={500} size="sm">
          Kanıt ekleri ({doluluk}/{AKSIYON_EK_LIMIT})
        </Text>
        {yukleyebilir && (
          <FileButton
            resetRef={sifirla}
            accept=".jpg,.jpeg,.png,.heic,.pdf"
            onChange={(dosya) => {
              if (!dosya) return;
              setYerelHata(null);
              // İstemci ön kontrolü yalnız kullanıcıyı boş yere beklet-memek
              // içindir; asıl kapı sunucudadır (imza + sınırlar, §22.4/2).
              if (dosya.size > DOSYA_MAX_BOYUT_BYTES) {
                setYerelHata('Dosya 10 MB sınırını aşıyor.');
                sifirla.current?.();
                return;
              }
              yukle.mutate(
                { actionId: aksiyon.id, dosya },
                { onSettled: () => sifirla.current?.() },
              );
            }}
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="light"
                data-testid="ek-yukle"
                loading={yukle.isPending}
              >
                Dosya ekle
              </Button>
            )}
          </FileButton>
        )}
      </Group>

      <Text size="xs" c="dimmed">
        jpg · png · heic · pdf — en çok 10 MB, aksiyon başına {AKSIYON_EK_LIMIT} dosya.
      </Text>
      {/* P13 — ek yükleme kapısı (sorumlu kimliği → açık aksiyon); sebep sunucudan. */}
      <KapiSebebi
        durum={aksiyon.yetkiler.ekKapisi}
        nesne="Kanıt dosyasını"
        fiil="yükler"
        testId="aksiyon-ek-yazamama-sebebi"
      />

      <QueryStatus queries={[ekler]} />
      {(yerelHata ?? (yukle.isError ? yukle.error.message : null) ?? null) !== null && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="ek-hata">
          {yerelHata ?? yukle.error?.message}
        </Text>
      )}
      {sil.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="ek-hata">
          {sil.error.message}
        </Text>
      )}

      {ekler.isSuccess && ekler.data.length === 0 && (
        <Text size="sm" c="dimmed" data-testid="ek-yok">
          Henüz kanıt dosyası yok.
        </Text>
      )}
      {ekler.isSuccess && ekler.data.length > 0 && (
        <Table data-testid="ek-listesi">
          <Table.Tbody>
            {ekler.data.map((ek) => (
              <EkSatiri
                key={ek.id}
                ek={ek}
                aksiyonId={aksiyon.id}
                // P13-c (madde 227) — silme kapısı SATIR BAŞINA sunucudan (şapka → kapalı aksiyon → sahiplik).
                silinebilir={ek.silmeKapisi.yazabilir}
                onIndir={() =>
                  indir.mutate({
                    actionId: aksiyon.id,
                    attachmentId: ek.id,
                    dosyaAdi: ek.dosyaAdi,
                  })
                }
                onSil={() => sil.mutate({ actionId: aksiyon.id, attachmentId: ek.id })}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}
      {ekler.isSuccess && (
        <SatirSilmeSebebi
          kapilar={ekler.data.map((ek) => ek.silmeKapisi)}
          nesne="Eki"
          fiil="siler"
          testId="ek-silme-sebebi"
        />
      )}
    </Stack>
  );
}

function EkSatiri({
  ek,
  silinebilir,
  onIndir,
  onSil,
}: {
  ek: ActionAttachmentDetail;
  aksiyonId: string;
  silinebilir: boolean;
  onIndir: () => void;
  onSil: () => void;
}): React.JSX.Element {
  return (
    <Table.Tr data-testid="ek-satiri">
      <Table.Td>
        <Anchor component="button" type="button" size="sm" onClick={onIndir} data-testid="ek-indir">
          {ek.dosyaAdi}
        </Anchor>
        <Text size="xs" c="dimmed">
          {ek.yukleyenEtiket} · {kbCinsinden(ek.boyutBytes)}
        </Text>
      </Table.Td>
      <Table.Td align="right">
        {silinebilir && (
          <Button
            size="xs"
            variant="subtle"
            color={ANLAM.engel}
            data-testid="ek-sil"
            onClick={onSil}
          >
            Sil
          </Button>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

function kbCinsinden(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${Math.round(bytes / 1024)} KB`;
}
